import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDownUp, Check, Pencil, ListFilter, Search } from "lucide-react";
import { api, getApiError } from "/src/api/client";
import RunStatusBadge from "../RunStatusBadge";
import DataTable from "../DataTable";
import { POLLING_STATUSES, STATUS_GROUPS } from "../../constants";


function RepoCell({ getValue }) {
  const url   = getValue();
  const label = url.replace(/\.git$/, "").split("/").slice(-2).join("/");
  return (
    <div className="relative group max-w-[196px]">
      <div className="truncate">{label}</div>
      <div className="absolute left-0 bottom-full m-2 z-50 hidden group-hover:flex items-center justify-center">
        <div className="bg-gray-200 text-slate text-xs rounded-lg px-3 py-1.5 shadow-xl">{url}</div>
      </div>
    </div>
  );
}


const DeploymentHistoryTable = () => {
  const navigate = useNavigate();
  const filterRef = useRef(null);

  const [runs, setRuns]                     = useState([]);
  const [_loading, setLoading]              = useState(true);
  const [statusFilter, setStatusFilter]     = useState("all");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [error, setError]                   = useState(null);
  const [searchInput, setSearchInput]       = useState("");
  const [search, setSearch]                 = useState("");
  const [sortOrder, setSortOrder]           = useState("desc");
  const searchDebounceRef                   = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target))
        setShowFilterMenu(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchInput]);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { sort_order: sortOrder };
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.search = search;
      const { data } = await api.get("/golive/deployment_requests", { params });
      setRuns(data?.items || []);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, sortOrder]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const pollRuns = useCallback(async () => {
    try {
      const params = { sort_order: sortOrder };
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.search = search;
      const { data } = await api.get("/golive/deployment_requests", { params });
      setRuns(data?.items || []);
    } catch (err) {
      console.error("Polling error", err);
    }
  }, [statusFilter, search, sortOrder]);

  const hasActiveRuns = runs.some((r) => POLLING_STATUSES.has(r.status));
  useEffect(() => {
    if (!hasActiveRuns) return;
    const interval = setInterval(pollRuns, 5000);
    return () => clearInterval(interval);
  }, [hasActiveRuns, pollRuns]);

  const filteredRuns = useMemo(() =>
    [...runs].sort((a, b) =>
      sortOrder === "asc"
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at)
    ),
  [runs, sortOrder]);

  const handleFilterChange = (value) => {
    setStatusFilter(value);
    setShowFilterMenu(false);
  };

  const columns = useMemo(() => [
    {
      accessorKey: "id",
      header: "Run",
      width: 60,
      align: "center",
      cell: ({ row }) => (
        <span className="text-sm font-semibold text-slate-700">{row.index + 1}</span>
      ),
    },
    {
      accessorKey: "id",
      id: "run_id",
      header: "Deployment ID",
      width: 120,
      cell: ({ getValue }) => {
        const id = getValue();
        return (
          <div className="relative group">
            <span className="font-mono text-xs text-slate-500">{id.slice(0, 8)}…</span>
            <div className="absolute left-0 bottom-full mb-1.5 z-50 hidden group-hover:block">
              <div className="bg-gray-200 text-slate text-xs rounded-lg px-3 py-1.5 shadow-xl whitespace-nowrap font-mono">
                {id}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "repo_url",
      header: "Repository",
      width: 220,
      cell: RepoCell,
    },
    {
      accessorKey: "target",
      header: "Target",
      width: 160,
      cell: ({ getValue }) => (getValue() === "gcp" ? "Google Cloud" : "Ubuntu VM"),
    },
    {
      accessorKey: "status",
      header: "Status",
      width: 180,
      sortAllowed: false,
      cell: ({ getValue }) => <RunStatusBadge status={getValue()} />,
    },
    {
      accessorKey: "created_at",
      header: "Started At",
      width: 190,
      cell: ({ getValue }) => new Date(getValue()).toLocaleString(),
    },
    {
      id: "actions",
      header: "Actions",
      width: 52,
      align: "center",
      sortAllowed: false,
      cell: ({ row }) => {
        const { id } = row.original;
        return (
          <div className="flex items-center justify-center">
            <button
              type="button"
              aria-label="View deployment"
              className="p-1.5 rounded-lg border-0 outline-none focus:outline-none focus-visible:outline-none text-slate-400 hover:text-slate-600 hover:bg-gray-200 transition-colors"
              onClick={() => navigate(`/golive/${id}`)}
            >
              <Pencil size={14} />
            </button>
          </div>
        );
      },
    },
  ], [navigate]);

  const toolbarContent = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        title={sortOrder === "desc" ? "Newest first — click for oldest first" : "Oldest first — click for newest first"}
        onClick={() => setSortOrder((s) => s === "desc" ? "asc" : "desc")}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-slate-500 hover:bg-gray-50 transition-colors text-xs whitespace-nowrap outline-none focus:outline-none focus-visible:outline-none"
      >
        <ArrowDownUp size={13} />
        {sortOrder === "desc" ? "Newest first" : "Oldest first"}
      </button>

      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by repo…"
          className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-300 w-48"
        />
      </div>

      <div className="relative" ref={filterRef}>
        <button
          type="button"
          onClick={() => setShowFilterMenu((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-slate-500 hover:bg-gray-50 transition-colors text-sm outline-none focus:outline-none focus-visible:outline-none"
        >
          <ListFilter size={15} />
          {statusFilter !== "all" && (
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
          )}
        </button>
        {showFilterMenu && (
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 min-w-48">
            <button
              type="button"
              onClick={() => handleFilterChange("all")}
              className={[
                "w-full flex items-center justify-between px-3 py-1 text-xs transition-colors border-0 outline-none focus:outline-none focus-visible:outline-none",
                statusFilter === "all"
                  ? "bg-blue-50 text-blue-600 font-medium"
                  : "bg-white text-slate-500 hover:bg-gray-200",
              ].join(" ")}
            >
              <span>All statuses</span>
              {statusFilter === "all" && <Check size={11} className="text-blue-500 shrink-0" />}
            </button>

            {STATUS_GROUPS.map((group) => (
              <div key={group.label}>
                <div className="border-t border-gray-100 mt-1 mb-0.5" />
                <p className="px-3 pt-1 pb-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                  {group.label}
                </p>
                {group.options.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleFilterChange(value)}
                    className={[
                      "w-full flex items-center justify-between gap-2 px-3 py-0.5 transition-colors border-0 outline-none focus:outline-none focus-visible:outline-none",
                      value === statusFilter ? "bg-blue-50" : "bg-white hover:bg-gray-200",
                    ].join(" ")}
                  >
                    <RunStatusBadge status={value} />
                    {value === statusFilter && <Check size={11} className="text-blue-500 shrink-0" />}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {error && <div className="gl-error mb-3">{error}</div>}

      <div className="bg-white border border-gray-200 rounded-xl">
        <DataTable
          title="Deployments"
          isSorted={false}
          canSearch={false}
          showTotalEntries={false}
          autoResetPageIndex={false}
          toolbarContent={toolbarContent}
          table={{ columns, rows: filteredRuns }}
        />
      </div>
    </>
  );
};

export default DeploymentHistoryTable;
